import { Trans } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import FoldableSection from '@/design_system/FoldableSection';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import { useActionTypes } from '@/models/actionType';
import { useIsUpdatingWorkshop } from '@/models/article';
import { useDefectTypes } from '@/models/defectType';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { ColorSizeWeight } from '@/routes/Requests/Request/Article/ColorSizeWeight';
import ProductInfo from '@/routes/Requests/Request/Article/ProductInfo';

import { EstimatedValue } from '../../Request/Article/EstimatedValue';
import { ProductAttributeValue } from '../../Request/Article/ProductAttributeValue';
import { RetailPrice } from '../../Request/Article/RetailPrice';
import { UniqueArticle } from '../../Request/Article/UniqueArticle';

import { ArticlePhotoUpload } from './ArticlePhotoUpload';

export const NewArticleDetails = () => {
  const { article, view, errors, productAttributes } = useArticleContext();

  return (
    <FoldableSection
      label={
        <Title as="h2">
          <Trans id="requests.new.articles.drawer.information.title">Information</Trans>
        </Title>
      }
      isInvalid={errors.details?.hasError}
    >
      <Box padding="1rem" gap="1rem" className="bg-neutral-0">
        {view.details.serialNumber.shown && <UniqueArticle />}

        <ProductInfo />

        {productAttributes?.map((productAttribute) => (
          <ProductAttributeValue key={productAttribute.id} productAttribute={productAttribute} />
        ))}

        <ColorSizeWeight />

        <RetailPrice />

        <EstimatedValue />

        <Stack row gap="1rem">
          {view.details.articlePhoto.shown && (
            <div style={{ flex: '1 1 200px' }}>
              <ArticlePhotoUpload secondaryActionIconOnly={!!article.product?.data.image} />
            </div>
          )}
        </Stack>
      </Box>
    </FoldableSection>
  );
};

export const useHasNoAvailableDefectsOrActions = () => {
  const { article, view } = useArticleContext();

  const defectsQueryEnabled =
    (view.defects.shown || view.initialCondition.shown) &&
    article.snapshot.articleDefects.length === 0 &&
    !!article.productAttributeValues.length;

  const { data: defects } = useDefectTypes(
    {
      articleId: article.id,
    },
    {
      enabled: defectsQueryEnabled,
      keepPreviousData: true,
    }
  );

  const hasNoAvailableDefects = defectsQueryEnabled && defects?.length === 0;

  const isUpdatingWorkshop = useIsUpdatingWorkshop(article.id);

  const actionsQueryEnabled =
    view.services.actions.shown &&
    (view.services.dispatchBeforeActions ? !isUpdatingWorkshop : true) &&
    article.snapshot.articleActions.length === 0 &&
    !!article.productAttributeValues.length;

  const { data: actionTypesData } = useActionTypes(
    {
      articleId: article.id,
    },
    {
      enabled: actionsQueryEnabled,
      keepPreviousData: true,
    }
  );

  const hasNoAvailableActions =
    actionsQueryEnabled &&
    actionTypesData?.actionTypes?.length === 0 &&
    actionTypesData?.packActionTypes?.length === 0;

  return {
    hasNoAvailableDefects,
    hasNoAvailableActions,
  };
};
